import React from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";
import { SliderPage } from ".";
import { useCatalogFloorplan, useProduct } from "../adapters/uplist";
import {
  FLOORPLAN_ROOM_SAMPLES,
  FLOORPLAN_SAMPLES,
} from "../assets/images/samples";
import { InternalSection } from "../components";
import {
  BlockCheckbox,
  HiddenProduct,
  InlineCheckbox,
  Input,
} from "../components/fields";
import { Notes } from "../components/fields/Notes";
import AddonAlert from "../components/order/AddOnAlert";
import AddOnSection from "../components/order/AddOnSection";
import FlickityCarousel from "../components/order/FlickityCarousel";
import { FloorplanAddons } from "../components/order/floorplan/FloorplanAddons";
import { useFpProduct } from "../hooks";
import useFullAddress from "../hooks/useFullAddress";

const FloorPlans = ({ children }) => {
  useFpProduct();
  const { data: cat_product } = useProduct("2501");
  const { setValue, watch } = useFormContext();
  const [cat_selected, fpSku] = watch(["cat_selected", "fpSku"]);
  const { data: fp_product } = useProduct(fpSku);
  const { data: catalogFp } = useCatalogFloorplan();

  const { floorplans } = catalogFp || {};
  const text = (
    <>
      <p>
        We have taken measurements at this address before! Would you like to
        have this existing plan rebranded for your use at a fantastic discount?
      </p>
      <p>We may need to revist the property if you purchase certain add-ons.</p>
    </>
  );

  const changed = ["2005"].includes(fpSku);

  const handleChanges = (sku) => {
    setValue("fpSku", sku);
    setValue(sku, true);
  };

  if (!catalogFp?.match_found) return children;
  return (
    <>
      {!cat_selected ? (
        <AddonAlert
          text={text}
          button_text={`Yes $${cat_product.price}`}
          handleButton={() => setValue("cat_selected", true)}
          display
        />
      ) : (
        <>
          {!fpSku ? (
            <Row>
              <Col xs={12}>
                <p>
                  Please look over these plans and let us know if there have
                  been any renovations. If there are, we will set an appointment
                  to visit the property to measure the updates, and you will
                  only be charged for the relevant areas.
                </p>
              </Col>
              <Col>
                <Button
                  onClick={() => handleChanges("2501")}
                  block
                  variant="outline-secondary"
                >
                  Purchase Floor Plan As-Is
                </Button>
              </Col>
              <Col>
                <Button onClick={() => handleChanges("2005")} block>
                  Purchase Floor Plan With Changes
                </Button>
              </Col>
              <Col xs={12} className="mt-3">
                <div
                  className="text-center text-link"
                  onClick={() => setValue("cat_selected", false)}
                >
                  I don't want a Floor Plan
                </div>
                {/* Hidden Product is just to prevent navigation when this section is showing. */}
                <HiddenProduct sku="hidden_cat_selected" type="text" required />
              </Col>
            </Row>
          ) : (
            <>
              {children}
              {changed && fp_product && (
                <Row>
                  <Col xs={12}>
                    <Notes
                      sku="notes.cat_floorplan"
                      label="Please tell us what needs to be edited.*"
                      required
                    />
                  </Col>
                </Row>
              )}
            </>
          )}
          {floorplans && (
            <FlickityCarousel
              urls={floorplans}
              id="catalogCarousel"
              className="my-3"
            />
          )}
        </>
      )}
    </>
  );
};
const FloorPlanProduct = () => {
  const [fpSku, sqftCharge] = useWatch({ name: ["fpSku", "sqftCharge"] });
  if (!fpSku) return null;
  return (
    <Row>
      <BlockCheckbox
        key={fpSku}
        sku={fpSku}
        label={`${
          ["2501", "2005"].includes(fpSku) ? " Catalogue" : "Basic"
        } Floor Plan${fpSku === "2005" ? " w/ Changes" : ""}`}
        additional_price={sqftCharge}
        modal={{ urls: FLOORPLAN_SAMPLES }}
      />
    </Row>
  );
};

const FloorPlansPage = () => {
  const { data: catalogFp } = useCatalogFloorplan();
  const { full_address } = useFullAddress();
  const waitForCatalogue = !catalogFp && !!full_address?.length;
  return (
    <SliderPage title="Floor Plans">
      {waitForCatalogue ? (
        <h2>Checking our catalogue for an existing floor plan...</h2>
      ) : (
        <FloorPlans>
          <FloorPlanProduct />
        </FloorPlans>
      )}
      <AddOnSection
        show_skus={["2001", "2005", "2501"]}
        addon_skus={["order_info.add_sq_ft"]}
      >
        <Input
          label="Estimated Unfinished Square Footage"
          type="number"
          sku="order_info.add_sq_ft"
          defaultValue={0}
          description="In order to provide a more accurate estimate, please let us know the approximate square footage of any unfinished areas. Example: Decks, Patios, Garages, Basement, etc."
        />
      </AddOnSection>
      <FloorplanAddons />
      <InternalSection>
        <InlineCheckbox sku="2011" modal={{ urls: FLOORPLAN_ROOM_SAMPLES }} />
        <InlineCheckbox sku="2990" />
        <InlineCheckbox sku="9810" />
        <Notes name="notes.floorplan" label="Floor Plan Appt Notes" />
      </InternalSection>
    </SliderPage>
  );
};

export default FloorPlansPage;
